/**
 * @file
 * Display a modal dialog box with a Markdown formatted message and Accept and Cancel buttons. When inserting
 * arbitrary strings such as a ticker symbol or watchlist name, escape the string using
 * [$markdown.escape](#/global/markdown).
 *
 * @example
 * $confirm('Are you **sure** you want to reformat your hard drive?', {
 *     verb: 'Reformat',
 *     tone: 'remove'
 * });
 * @example
 * $confirm('A horse is a horse.', {
 *     verb: 'Of Course',
 *     dismissable: true,
 *     action: () => $notify('I am Mr. Ed')
 * });
 */


import alert from '../components/private/alert.vue';


let uniqueKey = 0;


/**
 * @param {string} msg - The message to display, with optional Markdown formatting.
 * @param {Object} options
 * @param {string} options.width - Width in any CSS units.
 * @param {string} options.height - Height in any CSS units.
 * @param {string} options.tone - Semantic color class applied to the button.
 * @param {string} options.key - A unique key that can be used to suppress additional confirmations with the same key, even
 * if their message is not identical.
 * @param {boolean} options.unique - If `true`, do not suppress additional confirmations even if the message is identical.
 * @param {string} options.verb - An alternative label for the button. This should almost always be specified, because
 * users are far more likely to read the button text than the dialog message in practice.
 * @param {boolean} options.dismissable - If `true`, show a "Do not show this message again" checkbox.
 * @param {func} options.action - Action to take on affirmative response. If `dismissable` is specified, a boolean
 * argument will be passed which is `true` if the box is checked, `false` if not checked.
 * @param {func} options.cancel - Action to take on cancel. If `dismissable` is specified, a boolean argument will be
 * passed which is `true` if the box is checked, `false` if not checked.
 */
function $confirm(msg, options = {}) {
    const host = window.__app;
    if (!host) {
        throw new Error('$confirm: no scope');
    }
    msg = '' + msg;
    const key = 'confirm:' + (options.key == null ? options.unique ? uniqueKey++ : msg : options.key);
    let entry = host.child.find(entry => entry.key === key);
    if (!entry) {
        host.child.push((entry = {
            is: alert,
            key,
            on: {
                close() {
                    Vue.delete(host.child, host.child.findIndex(e => e.key === key));
                }
            }
        }));
    }
    Vue.set(entry, 'attrs', {
        msg,
        type: 'confirm',
        ...options
    });
}


export default $confirm;
